import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetApprovalAllType, useGetFilterRecommend } from '@/hooks/apiHooks';
import CheckBox from '@/components/Approval/LeftContainer/CategoryFilter/CheckBox';
import { Dialog } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import ApprovalCategoryFilters from '@/components/Approval/ApprovalCategoryFilters';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { approvalFilter, approvalTempFilter } from '@/recoil/atom';
import { ApprovalType } from '@/types/api';
import CategorySearch from '@/components/Approval/LeftContainer/CategoryFilter/CategorySearch';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';

const filterName = 'types';

type P = {
  pageArea: WorkPageAreaType;
  title: string;
};

/**
 * CategoryFilter(양식) 의 편집 본문(검색 + 선택/최근 리스트 + 전체보기 모달).
 * FilterChipPopover 안에서 호스팅.
 */
const CategoryFilterPanel = ({ pageArea, title }: P) => {
  const workEventTracker = useWorkEventTracker(pageArea);
  const [filters, setFilters] = useAtom(approvalFilter);
  const [tempFilters, setTempFilters] = useAtom(approvalTempFilter);
  const [modalOpen, setModalOpen] = useState(false);

  // 팝오버를 (다시) 열 때 목록을 현재 선택값 기준으로 리셋 — 해제 후 재오픈 시 잔여 항목 방지
  useEffect(() => {
    setTempFilters((prev) => ({ ...prev, [filterName]: filters[filterName] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const methods = useForm({
    defaultValues: { keyword: '', [filterName]: [] },
  });

  const { data, isLoading: isTypeLoading } = useGetApprovalAllType();

  const { t } = useTranslation();

  const handleModalOpen = () => {
    setModalOpen(true);
    // GA Work - Approval 11번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: title,
      contents: '전체보기',
      status: undefined,
      description: '',
    });
  };
  const handleModalClose = () => {
    setModalOpen(false);
  };

  // 검색 중이면 기본(최근/전체보기) 목록을 숨기고 인라인 검색결과만 노출
  const searching = !!methods.watch('keyword');

  const { data: recommendData, isLoading: isRecommendLoading } =
    useGetFilterRecommend();

  // 양식 목록/추천 응답 전에는 목록 자리가 빈 공백으로 보이므로 스켈레톤으로 채운다
  const isRecommendPending = isTypeLoading || isRecommendLoading;
  const hasSelected = (tempFilters[filterName] || []).length > 0;

  //typeNum To Object Array
  const recommend =
    recommendData?.types
      ?.map((typeNum) => data?.filter((res) => res.type === typeNum) || [])
      .flat() || [];

  const recommendItems =
    recommend.filter(
      (item1) =>
        !(tempFilters[filterName] || []).some(
          (item2) => item1.type === item2.type,
        ),
    ) ?? [];

  const handleChange = (approval: ApprovalType) => {
    const { type, name, tag, sortNumber } = approval;
    const isChecked = filters[filterName].some(
      (res: ApprovalType) => res.type === type,
    );
    if (isChecked) {
      setFilters({
        ...filters,
        [filterName]: filters[filterName].filter((res) => res.type !== type),
      });
    } else {
      setFilters({
        ...filters,
        [filterName]: [...filters[filterName], { type, tag, name, sortNumber }],
      });
    }

    // GA Work - Approval 10번
    workEventTracker(WorkEventName.clickEvent, {
      area_name: title,
      contents: name,
      status: !isChecked,
      description: '품의 필터',
    });

    //TEMP_FILTER
    if (!(tempFilters[filterName] || []).some((item) => item.type === type)) {
      setTempFilters({
        ...tempFilters,
        types: [
          ...(tempFilters[filterName] || []),
          { type, tag, name, sortNumber },
        ],
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <CategorySearch
        pageArea={pageArea}
        filterName={filterName}
        handleChange={handleChange}
      />
      {!searching && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '8px',
            }}
          >
            {(tempFilters[filterName] || []).map((res) => (
              <CheckBox
                key={res.type}
                {...res}
                filterName={filterName}
                handleChange={handleChange}
              />
            ))}
            {hasSelected && <Divider />}
            <RecentLabel>{t('최근')}</RecentLabel>
            {isRecommendPending ? (
              <RecommendSkeleton />
            ) : recommendItems.length > 0 ? (
              recommendItems.map((res) => (
                <CheckBox
                  key={res.type}
                  {...res}
                  filterName={filterName}
                  handleChange={handleChange}
                />
              ))
            ) : (
              // 로딩이 끝났는데 추천이 비면 섹션째 사라져 반대 방향으로 높이가 튄다
              <EmptyRecent>{t('최근 사용한 양식이 없어요.')}</EmptyRecent>
            )}
          </div>
          <div style={{ height: '1px', background: 'var(--dk-bg-strong, #e7e9ec)' }} />
          <button
            style={{
              width: '100%',
              padding: '12px',
              textAlign: 'center',
              fontSize: '14px',
              color: 'var(--dk-text, black)',
              background: 'var(--dk-bg, white)',
            }}
            onClick={handleModalOpen}
          >
            {t('전체보기')}
          </button>
        </>
      )}
      <Dialog
        fullWidth
        open={modalOpen}
        onClose={handleModalClose}
        sx={{
          '& .MuiDialog-paper': {
            width: '1168px', // 원하는 너비
            padding: '16px 24px 32px 24px',
            maxWidth: 'none', // 기본 maxWidth를 비활성화
            height: 'calc(100vh - 80px)',
            margin: '0',
            borderRadius: '8px',
          },
        }}
      >
        <ApprovalCategoryFilters
          pageArea={pageArea}
          handleClose={handleModalClose}
        />
      </Dialog>
    </FormProvider>
  );
};

export default CategoryFilterPanel;

/**
 * '최근' 목록 로딩 자리 — 항목 행 높이(28px)를 그대로 채워 열림 직후 빈 공백을 막는다.
 * 줄 수는 서버가 내려주는 추천 개수(RecommendFilterResponse.types)에 상한이 없어
 * 실제로 가장 흔한 5개에 맞췄다. 개수가 다르면 로딩 완료 시 그만큼 높이가 조정된다.
 */
const RecommendSkeleton = () => (
  <Skeleton variant={'rect'} width={'100%'} height={28} count={5} gap={'8px'} />
);

/** 선택 목록과 '최근' 사이 구분선 — 로딩 중에도 같은 자리를 차지해야 시프트가 없다 */
const Divider = styled.div`
  height: 1px;
  background: var(--dk-bg-strong, #e7e9ec);
  margin: 0 -8px;
`;

const RecentLabel = styled.div`
  color: var(--dk-text-secondary, #888d96);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

const EmptyRecent = styled.div`
  color: var(--dk-text-dim, #b7bbc2);
  font-size: 14px;
  line-height: 20px;
  padding: 4px 0;
`;
